import moment from "moment-timezone";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";

import type { Addendum, NoteDetail, PatientDetails, UUID } from "@/core/types";
import { admin_cse } from "@/config/roles";
import { getQueryParams } from "@/utils/filters";
import images from "@/utils/images";
import { isAuthorized } from "@/utils/authToken";
import { sharedRequestsTyped } from "@/api/(requests)/sharedTyped";
import { showAlertWithAction } from "@/redux/actions/alerts";

import { AddendumDisplay } from "./components/(addendum)/components/AddendumDisplay";
import { NoteBadges } from "./components/NoteBadges";
import { NoteContentRouter } from "./components/NoteContentRouter";
import { SignatureList } from "./components/(signature)/components/SignatureList";

import "./PrintNoteView.styles.scss";

export function PrintNoteView() {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { patientId } = useParams<{ patientId: UUID }>();
  const [notes, setNotes] = useState<NoteDetail[] | undefined>(undefined);
  const [user, setUser] = useState<PatientDetails | undefined>(undefined);
  const [addendums, setAddendums] = useState<Record<string, Addendum[]> | undefined>(undefined);

  useEffect(() => {
    (async () => {
      try {
        let noteIds = getQueryParams("noteIds", location.search.trim());

        if (!noteIds) {
          return;
        }

        let noteIdList = noteIds.split(",");

        if (noteIdList.length > 50) {
          dispatch(
            showAlertWithAction(
              "Too many notes selected for export. Please reduce the selection and try again (max: 50).",
            ),
          );

          return;
        }

        await getPatient();

        await getNotes(noteIdList);

        await getNoteAddendums(noteIdList);
      } catch (e) {
        console.log(e);
      }
    })();
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- route params won't change while mounted
  }, []);

  useEffect(() => {
    if (user !== undefined && notes !== undefined && addendums !== undefined) {
      document.title = `${user.first_name} ${user.last_name} ${notes.length} Notes -
            ${moment().local().format("YYYY-MM-DD HH:mm")}`;
      window.print();
    }
  }, [user, notes, addendums]);

  const getPatient = async () => {
    try {
      const urlParams = { patientId };
      const patientResponse = await sharedRequestsTyped.fetch_patient_details({ urlParams });

      setUser(patientResponse.user);
    } catch (e) {
      console.log(e);
      dispatch(showAlertWithAction("Error fetching patient information."));
    }
  };

  const getNotes = async (noteIds: string[]) => {
    try {
      const urlParams = { patientId };
      const notes = isAuthorized(admin_cse)
        ? await sharedRequestsTyped.fetch_patient_progress_notes_batch({
            searchParams: { note_ids: noteIds as UUID[] },
            urlParams,
          })
        : [
            await sharedRequestsTyped.fetch_patient_progress_notes({
              searchParams: { note_id: noteIds[0] as UUID },
              urlParams,
            }),
          ];

      setNotes(
        notes.sort(
          (a, b) =>
            +moment(b.session_date + " " + (b.session_from ?? "00:00"), "MM/DD/YYYY HH:mm") -
            +moment(a.session_date + " " + (a.session_from ?? "00:00"), "MM/DD/YYYY HH:mm"),
        ),
      );
    } catch (e) {
      console.log(e);
      dispatch(showAlertWithAction("Error fetching notes to print."));
    }
  };

  const getNoteAddendums = async (noteIds: string[]) => {
    try {
      const urlParams = { patientId };
      const body = isAuthorized(admin_cse)
        ? { note_ids: noteIds as UUID[], patient_id: patientId }
        : { note_id: noteIds[0] as UUID, patient_id: patientId };

      const addendumResponse = await sharedRequestsTyped.get_note_addendums({
        body,
        urlParams,
      });

      const newAddendums = addendumResponse.addendums.reduce<Record<string, Addendum[]>>(
        (obj, addendum) => {
          const noteId = addendum.note_id;

          if (obj[noteId] === undefined) {
            obj[noteId] = [];
          }

          obj[noteId].push(addendum);

          return obj;
        },
        {},
      );

      setAddendums(newAddendums);
    } catch (e) {
      console.log(e);
      dispatch(showAlertWithAction("Error fetching note addendums."));
    }
  };

  const displayHeader = () => (
    <div className="print-note-heading d-flex justify-content-between">
      <div>
        <div className="fs-22 tw:font-bold" data-private>
          {user!.first_name} {user!.last_name}
        </div>

        <div>Date of Birth: {user!.dob}</div>
      </div>

      <div className="text-right">
        <div>{notes!.length} Notes</div>
        <div>Exported on: {moment().local().format("DD/MM/YYYY")}</div>
      </div>
    </div>
  );

  const displayNoteHeader = (note: NoteDetail) => {
    if (note.note_type === "Care Management" || note.note_type === "Advanced Care Activity") {
      return null;
    }

    return (
      <div className="print-note-heading">
        <p className="tw:font-bold">{note.title || `${note.note_type} ${note.session_date}`}</p>
      </div>
    );
  };

  return (
    <>
      <div className="d-flex justify-content-between print-class" style={{ padding: "20px 100px" }}>
        <button
          className="cursor d-flex tw:border-0 tw:bg-transparent tw:p-0"
          onClick={() => history.push(`/app/patient/${patientId}/profile/v2?selectedTab=Notes`)}
          type="button"
        >
          <img alt="<" src={images("./icons/common/chevron-left-secondary.svg")} />
          <div className="txt-sec ml-2" style={{ height: 20, lineHeight: "20px" }}>
            Patient Profile
          </div>
        </button>

        <button
          className="cursor d-flex tw:border-0 tw:bg-transparent tw:p-0"
          onClick={() => window.print()}
          type="button"
        >
          <img
            alt="print icon"
            src={images("./icons/common/print-secondary.svg")}
            style={{ height: "20px" }}
          />

          <div className="txt-sec ml-2" style={{ height: 20, lineHeight: "20px" }}>
            Print
          </div>
        </button>
      </div>

      <div className="print-note">
        {user !== undefined && notes !== undefined && displayHeader()}

        {user !== undefined &&
          addendums !== undefined &&
          notes !== undefined &&
          notes.map((note, index) => {
            const patientName = `${user.first_name} ${user.last_name}`;

            return (
              <div className="pb-5 mb-5" key={note.id || index}>
                <hr className="Hr mb-2" style={{ borderColor: "black" }} />

                {displayNoteHeader(note)}

                <NoteBadges className="tw:mb-5 tw:list-none" note={note} />
                <NoteContentRouter note={note} patientName={patientName} />

                {addendums[note.id] &&
                  addendums[note.id].map((addendum, addendumIndex) => (
                    <AddendumDisplay
                      credentials={addendum.credentials}
                      data={addendum.addendum}
                      key={addendum.addendum_id ?? addendumIndex}
                      signature={addendum.signature}
                      style={{ marginBottom: 10, minHeight: 0 }}
                      textTestId={undefined}
                    />
                  ))}

                <SignatureList note={note} />
              </div>
            );
          })}
      </div>
    </>
  );
}
